import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Button,
  Chip,
  Stack
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import { Opinion } from '../../interface/Opinion';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface OpinionCardProps {
  opinion: Opinion;
  currentUserId?: string;
  onVote: (opinionId: string, voteType: 'agree' | 'disagree') => void;
  voting: boolean;
}

const OpinionCard: React.FC<OpinionCardProps> = ({
  opinion,
  currentUserId,
  onVote,
  voting
}) => {
  const navigate = useNavigate();
  const isTrending = (opinion.agreeCount || 0) + (opinion.disagreeCount || 0) > 10;
  const agreePercentage = opinion.agreeCount + opinion.disagreeCount > 0
    ? Math.round((opinion.agreeCount / (opinion.agreeCount + opinion.disagreeCount)) * 100)
    : 0;

  const handleCardClick = () => {
    navigate(`/opinions/${opinion._id}`);
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking on actions
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ xs: 1.5, sm: 2 }} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }} flexWrap="wrap">
            <Avatar
              sx={{
                width: { xs: 36, sm: 40, md: 44 },
                height: { xs: 36, sm: 40, md: 44 },
                bgcolor: 'primary.main',
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' }
              }}
            >
              {opinion.username.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body1" fontWeight={500} sx={{ fontSize: '0.9375rem' }}>
                  {opinion.username}
                </Typography>
                {opinion.category && (
                  <Chip
                    label={opinion.category}
                    size="small"
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'primary.dark',
                      height: 24,
                      fontSize: '0.8125rem',
                      fontWeight: 600
                    }}
                  />
                )}
                {opinion.isPublic && (
                  <Chip
                    icon={<PublicIcon sx={{ fontSize: 14 }} />}
                    label="Public"
                    size="small"
                    sx={{
                      bgcolor: 'info.light',
                      color: 'info.dark',
                      height: 24,
                      fontSize: '0.8125rem',
                      fontWeight: 500
                    }}
                  />
                )}
                {isTrending && (
                  <Chip
                    icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                    label="Trending"
                    sx={{
                      bgcolor: 'warning.light',
                      color: 'warning.dark',
                      height: 24,
                      fontSize: '0.8125rem'
                    }}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {formatDistanceToNow(new Date(opinion.createdAt), { addSuffix: true })}
                </Typography>
                {opinion.targetAudience && opinion.targetAudience.length > 0 && (
                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Seeking:
                    </Typography>
                    {opinion.targetAudience.slice(0, 2).map((audience, idx) => (
                      <Chip
                        key={idx}
                        label={audience}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          bgcolor: 'secondary.light',
                          color: 'secondary.dark'
                        }}
                      />
                    ))}
                    {opinion.targetAudience.length > 2 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        +{opinion.targetAudience.length - 2}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            mb: 2.5,
            color: 'text.primary',
            fontSize: '0.9375rem'
          }}
        >
          {opinion.content}
        </Typography>

        {/* Stats Bar */}
        {opinion.agreeCount + opinion.disagreeCount > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                mb: 0.5
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${agreePercentage}%`,
                  bgcolor: 'success.main',
                  borderRadius: 2,
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="success.main" sx={{ fontSize: '0.875rem' }}>
                {agreePercentage}% Agree
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ fontSize: '0.875rem' }}>
                {100 - agreePercentage}% Disagree
              </Typography>
            </Box>
          </Box>
        )}

        {/* Actions */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          pt={{ xs: 1.25, sm: 1.5 }}
          borderTop="1px solid"
          borderColor="divider"
          gap={{ xs: 1, sm: 1.5 }}
          flexWrap="wrap"
        >
          <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} onClick={handleActionClick} alignItems="center">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onVote(opinion._id, 'agree');
              }}
              disabled={voting || !currentUserId}
              sx={{
                bgcolor: opinion.agreeCount > 0 ? 'success.light' : 'transparent',
                color: 'success.main',
                minWidth: { xs: 40, sm: 36 },
                minHeight: { xs: 40, sm: 36 },
                touchAction: 'manipulation',
                '&:hover': {
                  bgcolor: 'success.light',
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <ThumbUpIcon fontSize="small" sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
            <Typography variant="body1" fontWeight={500} color="success.main" sx={{ minWidth: { xs: 28, sm: 24 }, fontSize: { xs: '0.8125rem', sm: '0.9375rem' } }}>
              {opinion.agreeCount || 0}
            </Typography>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onVote(opinion._id, 'disagree');
              }}
              disabled={voting || !currentUserId}
              sx={{
                bgcolor: opinion.disagreeCount > 0 ? 'error.light' : 'transparent',
                color: 'error.main',
                ml: { xs: 0.25, sm: 0.5 },
                minWidth: { xs: 40, sm: 36 },
                minHeight: { xs: 40, sm: 36 },
                touchAction: 'manipulation',
                '&:hover': {
                  bgcolor: 'error.light',
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <ThumbDownIcon fontSize="small" sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
            <Typography variant="body1" fontWeight={500} color="error.main" sx={{ minWidth: { xs: 28, sm: 24 }, fontSize: { xs: '0.8125rem', sm: '0.9375rem' } }}>
              {opinion.disagreeCount || 0}
            </Typography>
          </Stack>

          <Button
            startIcon={<CommentIcon fontSize="small" sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/opinions/${opinion._id}`);
            }}
            size="small"
            sx={{
              color: 'primary.main',
              textTransform: 'none',
              minWidth: { xs: 40, sm: 'auto' },
              minHeight: { xs: 40, sm: 36 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              touchAction: 'manipulation',
              '&:hover': {
                bgcolor: 'action.hover'
              },
              '&:active': {
                transform: 'scale(0.95)'
              }
            }}
          >
            {opinion.commentCount || 0}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OpinionCard;
